import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
	children: React.ReactNode;
	requireRole?: 'admin' | 'employee' | 'superadmin';
}

/**
 * Protected Route - requires authentication and content rights acceptance.
 * Waits for the startup auth check (token refresh) before making a routing decision.
 * Redirects to /login if not authenticated.
 * Redirects to /accept-terms if content rights not accepted (employees only).
 * When requireRole is set, redirects to /dashboard if the user's role doesn't match.
 */
export default function ProtectedRoute({ children, requireRole }: ProtectedRouteProps) {
	const location = useLocation();
	const { isAuthenticated, user } = useAuth();

	// Note: the startup auth check (isLoading) is handled by AuthGate in App.tsx,
	// so by the time this renders the refresh has already resolved.
	if (!isAuthenticated) {
		return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
	}

	if (requireRole && user?.role !== requireRole) {
		return <Navigate to='/dashboard' replace />;
	}

	const isPrivileged = user?.role === 'admin' || user?.role === 'superadmin';
	const hasAcceptedContentRights = user?.hasAcceptedContentRights === true;
	const isOnAcceptTermsPage = location.pathname === '/accept-terms';

	if (!hasAcceptedContentRights && !isOnAcceptTermsPage && !isPrivileged) {
		return <Navigate to='/accept-terms' replace />;
	}

	return <>{children}</>;
}
